import * as yup from 'yup';
import { createI18n } from 'vue-i18n';
import messages from '../i18n';

const i18n = createI18n({
  locale: 'en',
  legacy: false,
  messages,
  globalInjection: true,
});
const { t } = i18n.global;

export type ChoiceTuple = [string | boolean | Date | null, string];

export interface IFormField {
  _id: number; // used to control the order of fields in the form
  name: string; // used to identify the field in the form and for form submission
  label: string; // used to explain the field's purpose to the user
  isRequired: boolean; // used to determine if the field is required
  isActiveField?: boolean; // can control the visibility of the field
  fieldType: FieldType; // used to determine the type of input field to render
  errorMessage?: string; // used to provide an error message if the field is invalid
  choices?: ChoiceTuple[]; // used for radio and select fields
  value?: string | boolean | Date | [] | null | undefined; // the actual value of the field for form submission
  customKey?: string; // used to provide a custom key for the custom user input field
  customValue?: string | boolean | Date | [] | null | undefined; // the value of the custom user input field if applicable
  customPlaceholder?: string; // used to provide a placeholder for the custom user input field
  customMaxLength?: number; // used to limit the number of characters in the custom user input field
  customIsValid?: boolean; // used to indicate if the custom user input field is valid
  customErrorMessage?: string; // used to provide an error message if the custom user input field is invalid
  valueType: FieldValueType; // used to determine the type of value to expect
  helpText?: string; // used to provide additional information to the user
  footerHelpText?: string; // used to provide additional information in the footer of the element
  onClick?: () => void; // used to provide a click handler for the field
  placeholder?: string; // used to provide a placeholder for the input field
  autocomplete?: string; // WCAG 1.3.5 autofill token passed to native inputs
  maxLength?: number; // used to limit the number of characters in the input field
  isValid?: boolean; // used to indicate if the field is valid
  minimumAge?: number; // used to validate the age of the user if the field is a (birth)datepicker for date of birth
  isTooYoung?: boolean; // set while a birthdatepicker's pick - even just a too-recent year, which never reaches value - is under minimumAge
}

// Used to determine the type of input field to render
export enum FieldType {
  TEXT = 'text',
  EMAIL = 'email',
  RADIO = 'radio',
  CUSTOMRADIO = 'customradio',
  CHECKBOX = 'checkbox',
  CHECKBOXGROUP = 'checkboxgroup',
  BASICSELECTBUTTON = 'selectbutton',
  MULTISELECT = 'multiselect',
  DROPDOWN = 'dropdown',
  DATEPICKER = 'datepicker',
  BIRTHDATEPICKER = 'birthdatepicker', // month and year dropdowns, for dates the user already knows (e.g. dob)
}

// Used to determine the type of value to expect
export enum FieldValueType {
  STRING = 'string',
  BOOLEAN = 'boolean',
  ARRAY = 'array',
  DATE = 'date',
}

// Default field object
export const fieldObject: IFormField = {
  _id: 0,
  name: '',
  label: '',
  isRequired: false,
  fieldType: FieldType.TEXT,
  errorMessage: 'invalid input',
  value: '',
  valueType: FieldValueType.STRING,
};